// ---------------------------------------------------------------------------
// LassoOverlay — rubber-band lasso selection on the canvas.
//
// Mouse-down on empty canvas area starts a lasso; drag draws a rectangle;
// mouse-up selects elements within the lasso area.
//
// z-index: 40 — below guide overlay (45) and selection overlay (50).
// ---------------------------------------------------------------------------

import { useState, useRef, useEffect } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { Rect } from '@/features/page-editor/store/types'

export function LassoOverlay() {
  const [lasso, setLasso] = useState<Rect | null>(null)
  const lassoRef = useRef<Rect | null>(null)
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const isPreview = useEditorStore((s) => s.isPreview)

  // Keep ref in sync with state for use in event handlers
  useEffect(() => {
    lassoRef.current = lasso
  }, [lasso])

  useEffect(() => {
    const canvasEl = containerRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    // Convert clientX/clientY to overlay-relative LOCAL coordinates.
    // The overlay is inside a scaled container (zoom transform), so we must
    // divide screen-space offsets by zoom to get correct CSS positions.
    const getOverlayCoords = (me: MouseEvent) => {
      const overlayRect = (containerRef.current ?? canvasEl).getBoundingClientRect()
      const zoom = useEditorStore.getState().zoom
      return {
        x: (me.clientX - overlayRect.left) / zoom,
        y: (me.clientY - overlayRect.top) / zoom,
      }
    }

    const handleMouseDown = (e: Event) => {
      const me = e as MouseEvent
      const target = me.target as HTMLElement
      // Only start lasso if clicking on canvas background, not on blocks/elements
      if (!target.dataset.canvasBg && !target.dataset.lassoArea) return
      if (me.button !== 0) return

      me.preventDefault() // Prevent native text selection
      const coords = getOverlayCoords(me)
      startRef.current = coords
      isDragging.current = false

      // Clear any existing selection when starting lasso
      useEditorStore.getState().clearSelection()
    }

    const handleMouseMove = (e: Event) => {
      if (!startRef.current) return
      const me = e as MouseEvent
      me.preventDefault() // Prevent native text selection during drag

      const current = getOverlayCoords(me)
      const x = Math.min(startRef.current.x, current.x)
      const y = Math.min(startRef.current.y, current.y)
      const width = Math.abs(current.x - startRef.current.x)
      const height = Math.abs(current.y - startRef.current.y)

      if (width > 5 || height > 5) {
        isDragging.current = true
        ;(canvasEl as HTMLElement).style.userSelect = 'none'
        const lassoRect: Rect = { x, y, width, height }
        setLasso(lassoRect)
        lassoRef.current = lassoRect
      }
    }

    const handleMouseUp = () => {
      ;(canvasEl as HTMLElement).style.userSelect = ''

      const currentLasso = lassoRef.current
      if (isDragging.current && startRef.current && currentLasso && currentLasso.width > 5 && currentLasso.height > 5) {
        const canvasContainer = containerRef.current?.closest('[data-testid="canvas-viewport"]')
        if (canvasContainer) {
          const overlayRect = (containerRef.current ?? canvasContainer).getBoundingClientRect()
          const zoom = useEditorStore.getState().zoom
          const elements = canvasContainer.querySelectorAll<HTMLElement>('[data-element-id]')
          const matchedIds: string[] = []
          let matchedBlockName: string | null = null

          elements.forEach((el) => {
            const elRect = el.getBoundingClientRect()
            // Convert screen-space element rects to local coords (matching lasso coords)
            const relRect: Rect = {
              x: (elRect.left - overlayRect.left) / zoom,
              y: (elRect.top - overlayRect.top) / zoom,
              width: elRect.width / zoom,
              height: elRect.height / zoom,
            }
            if (rectsIntersect(currentLasso, relRect)) {
              const id = el.getAttribute('data-element-id')
              if (id) {
                matchedIds.push(id)
                if (!matchedBlockName) {
                  const blockEl = el.closest('[data-block-name]')
                  matchedBlockName = blockEl?.getAttribute('data-block-name') ?? null
                }
              }
            }
          })

          if (matchedIds.length > 0) {
            // Select only leaf elements (avoid selecting both parent and child)
            const leafIds = matchedIds.filter((id) => {
              const el = canvasContainer.querySelector(`[data-element-id="${id}"]`)
              if (!el) return true
              const descendants = el.querySelectorAll('[data-element-id]')
              for (const desc of descendants) {
                const descId = desc.getAttribute('data-element-id')
                if (descId && matchedIds.includes(descId)) return false
              }
              return true
            })
            useEditorStore.getState().selectMultiple(
              leafIds.length > 0 ? leafIds : matchedIds,
              matchedBlockName ?? undefined,
            )
          }
        }
      }

      startRef.current = null
      isDragging.current = false
      setLasso(null)
      lassoRef.current = null
    }

    canvasEl.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      canvasEl.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      ;(canvasEl as HTMLElement).style.userSelect = ''
    }
  }, [])

  if (isPreview) return null

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{ zIndex: 40, pointerEvents: 'none' }}
      data-testid="lasso-overlay"
    >
      {lasso && lasso.width > 2 && lasso.height > 2 && (
        <div
          className="absolute rounded-sm border border-primary/50 bg-primary/10"
          style={{
            left: lasso.x,
            top: lasso.y,
            width: lasso.width,
            height: lasso.height,
            pointerEvents: 'none',
          }}
          data-testid="lasso-rect"
        />
      )}
    </div>
  )
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width <= b.x ||
    b.x + b.width <= a.x ||
    a.y + a.height <= b.y ||
    b.y + b.height <= a.y
  )
}
